import { useState } from 'react';
import './blog.css';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  date: string;
  author: string;
  category: string;
  readTime: string;
}

const blogPosts: BlogPost[] = [
  {
    id: '1',
    title: 'Getting Started with Tamagui',
    excerpt: 'Learn how to build cross-platform apps with Tamagui and React Native. Discover the power of unified styling across web and mobile platforms.',
    date: 'Jan 10, 2026',
    author: 'John Doe',
    category: 'Tutorial',
    readTime: '5 min read',
  },
  {
    id: '2',
    title: 'Building a Monorepo with Bun',
    excerpt: 'A complete guide to setting up a monorepo workspace using Bun package manager. Learn workspace configuration and dependency management.',
    date: 'Jan 8, 2026',
    author: 'Jane Smith',
    category: 'DevOps',
    readTime: '8 min read',
  },
  {
    id: '3',
    title: 'Deploying to Cloudflare Workers',
    excerpt: 'Step-by-step tutorial for deploying serverless functions to Cloudflare. Build and scale applications without managing infrastructure.',
    date: 'Jan 5, 2026',
    author: 'Bob Wilson',
    category: 'Deployment',
    readTime: '6 min read',
  },
  {
    id: '4',
    title: 'Authentication Best Practices 2026',
    excerpt: 'Comprehensive guide to implementing secure authentication. Explore JWT, OAuth 2.0, and modern security patterns for your applications.',
    date: 'Jan 2, 2026',
    author: 'Alice Johnson',
    category: 'Security',
    readTime: '10 min read',
  },
  {
    id: '5',
    title: 'React Performance Optimization',
    excerpt: 'Master performance optimization techniques in React. Learn about memoization, code splitting, and lazy loading for faster applications.',
    date: 'Dec 28, 2025',
    author: 'Charlie Brown',
    category: 'Performance',
    readTime: '7 min read',
  },
  {
    id: '6',
    title: 'TypeScript Advanced Patterns',
    excerpt: 'Dive deep into advanced TypeScript patterns. Discover generics, conditional types, and template literal types for type-safe code.',
    date: 'Dec 25, 2025',
    author: 'Diana Lee',
    category: 'TypeScript',
    readTime: '9 min read',
  },
];

const getCategoryColor = (category: string): string => {
  const colors: Record<string, string> = {
    Tutorial: '#2a7cff',
    DevOps: '#10b981',
    Deployment: '#f59e0b',
    Security: '#ef4444',
    Performance: '#8b5cf6',
    TypeScript: '#06b6d4',
  };
  return colors[category] || '#2a7cff';
};

export const Blog = () => {
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  return (
    <div className="blog-container">
      {/* Header */}
      <div className="blog-header">
        <h1 className="blog-title">Blog</h1>
        <p className="blog-subtitle">
          Latest news, tutorials, and updates from our team. Explore deep dives into modern development practices.
        </p>
      </div>

      {/* Blog Grid */}
      <div className="blog-grid">
        {blogPosts.map((post) => (
          <BlogPostCard
            key={post.id}
            post={post}
            isHovered={hoveredCard === post.id}
            onHover={(id) => setHoveredCard(id)}
            onHoverEnd={() => setHoveredCard(null)}
          />
        ))}
      </div>
    </div>
  );
};

interface BlogPostCardProps {
  post: BlogPost;
  isHovered: boolean;
  onHover: (id: string) => void;
  onHoverEnd: () => void;
}

const BlogPostCard = ({ post, isHovered, onHover, onHoverEnd }: BlogPostCardProps) => (
  <div
    className={`blog-post-card ${isHovered ? 'hovered' : ''}`}
    onMouseEnter={() => onHover(post.id)}
    onMouseLeave={onHoverEnd}
  >
    {/* Card Header with Category */}
    <div className="blog-post-header">
      <span
        className="blog-category"
        style={{ borderColor: getCategoryColor(post.category) }}
      >
        {post.category}
      </span>
      <span className="blog-read-time">{post.readTime}</span>
    </div>

    {/* Decorative Gradient Bar */}
    <div
      className="blog-card-accent"
      style={{ backgroundColor: getCategoryColor(post.category) }}
    />

    {/* Content */}
    <div className="blog-post-content">
      <h3 className="blog-post-title">{post.title}</h3>
      <p className="blog-post-excerpt">{post.excerpt}</p>
    </div>

    {/* Meta Information */}
    <div className="blog-post-meta">
      <div className="blog-post-author">
        <div className="author-avatar">{post.author.charAt(0)}</div>
        <div className="author-info">
          <p className="author-name">{post.author}</p>
          <p className="author-date">{post.date}</p>
        </div>
      </div>
      <a className="blog-read-link" href="#read">
        Read →
      </a>
    </div>
  </div>
);
